import { signatureBase64 } from "../../logos";
import pdfMake from "pdfmake/build/pdfmake";
import { pdfMake as pdfMakeFonts } from "../../vfs_fonts";
import { forwardRef, useImperativeHandle } from "react";
import { PAY_AS_SUBMITTED_SENTINEL } from "../../components/enrollments/enrollment-fields";

// PDF styling constants for consistency
const PDF_STYLES = {
    fontSize: {
        title: 22,
        subtitle: 16,
        sectionHeader: 13,
        subsectionHeader: 11,
        body: 10,
        small: 9,
        tableCell: 8,
        footnote: 7
    },
    spacing: {
        sectionGap: 20,
        paragraphGap: 10,
        itemGap: 6,
        tableCell: 5
    },
    colors: {
        primary: '#1e85e5',
        textDark: '#1a1a1a',
        textMuted: '#666666',
        textLight: '#888888',
        borderLight: '#e0e0e0',
        backgroundAlt: '#f8f9fa',
        backgroundLight: '#fafbfc'
    }
};

const PDFGenerator = forwardRef((({
    selectedPlan, categories, employees, companyInfo, accountConfig, policyName, policyType, selectedPADOption,
    isDrawing, signature, imageURL, adminFee, enrollmentFee, userLogo, userColor,
    requiresBackdatingAck, backdatingAck
}, ref) => {
    const policyTypeLabel = policyType === "businessOwner"
        ? "Business Owner(s) & Dependents"
        : policyType === "employees"
            ? "Employees"
            : "";
    const formatLimit = (value, type) => {
        if (value === undefined || value === null || value === '') return '';
        return type === 'percent' ? `${value}% of salary` : `$${value}`;
    };
    const formatPhoneNumber = (value) => {
        const digits = (value || '').replace(/\D/g, '');
        if (digits.length !== 10) return value || '';
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    };
    const signatureImage = signatureBase64;
    let header = "Enrollment Form" + selectedPlan;
    let planLabel = "";
    let nhcColor = "#1e85e5";
    const fillColor = userColor ? userColor : nhcColor;

    if (selectedPlan === "hsa") {
        header = "Health Spending Account Enrollment Form";
        planLabel = " (HSA)";
    } else if (selectedPlan === "lsa") {
        header = "Lifestyle Spending Account Enrollment Form";
        planLabel = " (LSA)";
    } else if (selectedPlan === "fixed") {
        header = "Health and Lifestyle Spending Account Enrollment Form";
        planLabel = " (HSA and LSA)";
    } else if (selectedPlan === "flex") {
        header = "Flexible Spending Account Enrollment Form";
        planLabel = " (Flex)";
    }

    // Stacked per-category blocks (DEV-1583)
    const buildCategoryBlock = (category, index, isLast) => {
        const isFixedCategory = selectedPlan === "fixed";
        const accentColor = '#2563eb';
        const displayName = category.categoryName === "Custom" ? category.customCategoryName : category.categoryName;

        const labelCell = (txt) => ({ text: txt, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted, bold: true, border: [false, false, false, false] });
        const valueCell = (txt) => ({ text: txt, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textDark, border: [false, false, false, false] });

        const catLimitType = category.limitType || 'dollar';
        const fmtLimit = (v) => {
            const out = formatLimit(v, catLimitType);
            return out === '' ? '—' : out;
        };

        const tripleValue = (s, c, f) => `${fmtLimit(s)} / ${fmtLimit(c)} / ${fmtLimit(f)}`;

        const rows = [];
        if (isFixedCategory) {
            rows.push(['HSA — Single / Couple / Family', tripleValue(category.limitSingleHSA, category.limitCoupleHSA, category.limitFamilyHSA)]);
            rows.push(['LSA — Single / Couple / Family', tripleValue(category.limitSingleLSA, category.limitCoupleLSA, category.limitFamilyLSA)]);
        } else {
            const limitLabel = `${(selectedPlan || 'Limit').toUpperCase()} — Single / Couple / Family`;
            rows.push([limitLabel, tripleValue(category.limitSingle, category.limitCouple, category.limitFamily)]);
            if (selectedPlan === 'flex') {
                const plans = [];
                if (category.hasHsa) plans.push('HSA');
                if (category.hasLsa) plans.push('LSA');
                if (category.hasRsp) plans.push('RSP');
                if (category.hasTfsa) plans.push('TFSA');
                rows.push(['Flex plans', plans.join(', ') || '—']);
                const splitParts = [];
                if (category.defaultSelectionSplit) splitParts.push(category.defaultSelectionSplit);
                if (category.defaultSelectionNote) splitParts.push(`(${category.defaultSelectionNote})`);
                rows.push(['Default split', splitParts.join(' ') || '—']);
            }
        }
        rows.push(['Waiting period', `${category.waitingPeriod || '0'} month(s)`]);
        rows.push(['Coverage rate', `${category.coverageRate ?? 100}%`]);
        rows.push(['Prorate existing', category.prorateExistingEmployees ? 'Yes' : 'No']);
        rows.push(['Prorate new hires', category.prorateNewHires ? 'Yes' : 'No']);
        if (selectedPlan !== 'hsa') {
            rows.push(['Allow LSA dependents', category.allowLsaDependents ? 'Yes' : 'No']);
        }
        if (category.scheduleCredits) {
            rows.push(['Schedule credits', `Yes; ${category.scheduleCreditsFrequency}`]);
        }

        const fieldTableBody = rows.map(([k, v]) => [labelCell(k), valueCell(v)]);

        const headerColumns = {
            columns: [
                {
                    width: 'auto',
                    text: [
                        { text: `${index + 1}. `, color: accentColor, bold: true, fontSize: PDF_STYLES.fontSize.subsectionHeader },
                        { text: displayName || '—', color: PDF_STYLES.colors.textDark, bold: true, fontSize: PDF_STYLES.fontSize.subsectionHeader },
                    ]
                },
                { width: '*', text: '' }
            ],
            margin: [0, 0, 0, 4]
        };

        const block = {
            table: {
                widths: [1.25, '*'],
                body: [[
                    { text: '', fillColor: accentColor, border: [false, false, false, false] },
                    {
                        border: [false, false, false, false],
                        stack: [
                            headerColumns,
                            category.jobDescription
                                ? { text: category.jobDescription, italics: true, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted, margin: [0, 0, 0, 6] }
                                : { text: '', margin: [0, 0, 0, 0] },
                            {
                                table: { widths: [180, '*'], body: fieldTableBody },
                                layout: { paddingLeft: () => 0, paddingRight: () => 6, paddingTop: () => 1, paddingBottom: () => 1, hLineWidth: () => 0, vLineWidth: () => 0 }
                            }
                        ]
                    }
                ]]
            },
            layout: { paddingLeft: (i) => i === 0 ? 0 : 8, paddingRight: (i) => i === 0 ? 0 : 8, paddingTop: () => 6, paddingBottom: () => 6, hLineWidth: () => 0, vLineWidth: () => 0 },
            margin: [0, 0, 0, 10]
        };

        if (isLast) {
            block.pageBreak = 'after';
        }
        return block;
    };

    const categoryBlocks = categories.map((c, i) => buildCategoryBlock(c, i, i === categories.length - 1));

    let configDesc = ["When the plan is to start. The plan can be backdated up to one year (will apply to all employees)", "The 12 month cycle that claims are made against.  You can align it to your fiscal year or keep it to a calendar year.", "Number of days from start of new benefit year during which claims can be made against the previous year.", "Credit Carry:  Unused credits from one benefit year can transfer to the next year after the runoff period has ended. Expense Carry:  Expenses (receipts) from one benefit year can be claimed in the next year, after the runoff period has ended. No Carry:  Credits must be used within each benefit year only. No carry forward after the selected run-off period", "Child dependents remain eligible until, and including, this age.", "Child dependents attending full-time post secondary school remain eligible until (and including) this age. ", "The date employees should receive their first Flex Selection emails.", "This date is simply the last day that employees are able to make their selection. After this date they will default to the policy's chosen Default split, which can be found in the assigned benefit category details."]

    let toTitleCase = (str) => {
        return str
            .replace(/([A-Z])/g, ' $1')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    let configTableBody = [];
    const entries = Object.entries(accountConfig);
    const total = entries.length - 2;

    for (let i = 0; i < total; i++) {
        const [key, value] = entries[i];
        configTableBody.push([
            { text: toTitleCase(key) },
            { text: value },
            { text: configDesc[i] }
        ]);
    }

    // Determine if an employee's assigned category uses % of salary, so we know whether to show salary
    const findAssignedCategory = (employee) => {
        const assigned = employee?.categoryAssign;
        if (!assigned) return null;
        return categories.find((c) => {
            const name = c.categoryName === "Custom" ? c.customCategoryName : c.categoryName;
            return name === assigned;
        }) || null;
    };
    const isPercentSalaryEmployee = (employee) => findAssignedCategory(employee)?.limitType === 'percent';
    const formatSalary = (value) => {
        if (value === undefined || value === null || value === '') return '';
        const num = Number(value);
        if (isNaN(num)) return `$${value}`;
        return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    };

    // Stacked per-employee blocks (DEV-1583)
    const employeeAccent = '#2563eb';
    const empBadgeBg = '#dbeafe';
    const empBadgeFg = '#1e40af';

    const empLabelCell = (txt) => ({ text: txt, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted, bold: true, border: [false, false, false, false] });
    const empValueCell = (txt) => ({ text: txt, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textDark, border: [false, false, false, false] });

    let employeeContent = employees.map((employee, index) => {
        const fullName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || '—';

        const empDetailRows = [
            [empLabelCell('Former last name'), empValueCell(employee.maidenName || '—')],
            [empLabelCell('Nickname'), empValueCell(employee.nickName || '—')],
            [empLabelCell('Email'), empValueCell(employee.email || '—')],
            [empLabelCell('Date of birth'), empValueCell(employee.birthDate || '—')],
            [empLabelCell('Hire date'), empValueCell(employee.hireDate || '—')],
        ];

        const headerColumns = {
            columns: [
                {
                    width: 'auto',
                    text: [
                        { text: `${index + 1}. `, color: employeeAccent, bold: true, fontSize: PDF_STYLES.fontSize.subsectionHeader },
                        { text: fullName, color: PDF_STYLES.colors.textDark, bold: true, fontSize: PDF_STYLES.fontSize.subsectionHeader },
                    ]
                },
                employee.categoryAssign ? {
                    width: 'auto',
                    margin: [8, 2, 0, 0],
                    table: {
                        body: [[{
                            text: (employee.categoryAssign || '').toUpperCase(),
                            fillColor: empBadgeBg, color: empBadgeFg, bold: true,
                            fontSize: PDF_STYLES.fontSize.footnote,
                            border: [false, false, false, false]
                        }]]
                    },
                    layout: { paddingLeft: () => 6, paddingRight: () => 6, paddingTop: () => 1, paddingBottom: () => 1, hLineWidth: () => 0, vLineWidth: () => 0 }
                } : { width: 'auto', text: '' },
                { width: '*', text: '' }
            ],
            margin: [0, 0, 0, 4]
        };

        if (isPercentSalaryEmployee(employee)) {
            empDetailRows.push([empLabelCell('Salary'), empValueCell(formatSalary(employee.salary) || '—')]);
        }

        const DEP_INDENT = 18;
        const dependentsLabel = {
            text: 'Dependents',
            fontSize: PDF_STYLES.fontSize.small,
            bold: true,
            color: PDF_STYLES.colors.textMuted,
            margin: [DEP_INDENT, 8, 0, 4]
        };

        const dependentBlocks = (employee.dependents || []).map((dep, depIndex) => {
            const depFullName = `${dep.firstName || ''} ${dep.lastName || ''}`.trim() || '—';
            const isStudent = dep.relation === 'Child (Post-secondary student)';

            const depDetailRows = [
                [empLabelCell('Former last name'), empValueCell(dep.maidenName || '—')],
                [empLabelCell('Nickname'), empValueCell(dep.nickName || '—')],
                [empLabelCell('Date of birth'), empValueCell(dep.birthDate || '—')],
                [empLabelCell('Relationship'), empValueCell(dep.relation || '—')],
            ];
            if (isStudent && dep.studentTermStart) {
                depDetailRows.push([empLabelCell('Student term start'), empValueCell(dep.studentTermStart)]);
            }
            if (isStudent && dep.studentTermEnd) {
                depDetailRows.push([empLabelCell('Student term end'), empValueCell(dep.studentTermEnd)]);
            }

            const headerLine = {
                columns: [
                    {
                        width: 'auto',
                        text: [
                            { text: `${depIndex + 1}. `, color: PDF_STYLES.colors.textMuted, fontSize: PDF_STYLES.fontSize.small, bold: true },
                            { text: depFullName, color: PDF_STYLES.colors.textDark, fontSize: PDF_STYLES.fontSize.small, bold: true },
                        ]
                    },
                    isStudent ? {
                        width: 'auto',
                        margin: [6, 1, 0, 0],
                        table: {
                            body: [[{
                                text: 'student',
                                fillColor: '#f1f5f9', color: '#64748b', bold: false,
                                fontSize: PDF_STYLES.fontSize.footnote,
                                border: [false, false, false, false]
                            }]]
                        },
                        layout: { paddingLeft: () => 5, paddingRight: () => 5, paddingTop: () => 0.5, paddingBottom: () => 0.5, hLineWidth: () => 0, vLineWidth: () => 0 }
                    } : { width: 'auto', text: '' },
                    { width: '*', text: '' }
                ],
                margin: [0, 0, 0, 2]
            };

            return {
                stack: [
                    headerLine,
                    {
                        table: {
                            widths: [120, '*'],
                            body: depDetailRows
                        },
                        layout: { paddingLeft: () => 0, paddingRight: () => 6, paddingTop: () => 1, paddingBottom: () => 1, hLineWidth: () => 0, vLineWidth: () => 0 }
                    }
                ],
                margin: [DEP_INDENT, 0, 0, 6]
            };
        });

        const noDependentsText = {
            text: 'No dependents',
            italics: true,
            fontSize: PDF_STYLES.fontSize.small,
            color: PDF_STYLES.colors.textMuted,
            margin: [DEP_INDENT, 6, 0, 0]
        };

        const dependentsSection = (employee.dependents && employee.dependents.length > 0)
            ? [dependentsLabel, ...dependentBlocks]
            : [noDependentsText];

        return {
            table: {
                widths: [1.25, '*'],
                body: [[
                    { text: '', fillColor: employeeAccent, border: [false, false, false, false] },
                    {
                        border: [false, false, false, false],
                        stack: [
                            headerColumns,
                            {
                                table: { widths: [140, '*'], body: empDetailRows },
                                layout: { paddingLeft: () => 0, paddingRight: () => 6, paddingTop: () => 1, paddingBottom: () => 1, hLineWidth: () => 0, vLineWidth: () => 0 }
                            },
                            ...dependentsSection,
                        ]
                    }
                ]]
            },
            layout: { paddingLeft: (i) => i === 0 ? 0 : 8, paddingRight: (i) => i === 0 ? 0 : 8, paddingTop: () => 6, paddingBottom: () => 6, hLineWidth: () => 0, vLineWidth: () => 0 },
            margin: [0, 0, 0, 10]
        };
    });

    const today = new Date();
    const day = today.getDate();
    const month = today.toLocaleString('default', { month: 'long' });
    const year = today.getFullYear();

    let indemnityContract = {
        stack: [
            {
                text: 'INDEMNITY CONTRACT',
                fontSize: 18,
                font: 'Merriweather',
                alignment: 'center',
                color: PDF_STYLES.colors.textDark,
                bold: true,
                margin: [0, 60, 0, 8]
            },
            {
                text: '—',
                fontSize: 20,
                alignment: 'center',
                color: fillColor,
                margin: [0, 0, 0, 16]
            },
            {
                text: 'Plan Services Contract Providing for the Administration of a Health Spending Account (Private Health Services Plan) and/or Lifestyle Reimbursement Account',
                fontSize: PDF_STYLES.fontSize.body,
                font: 'SourceSansPro',
                alignment: 'center',
                color: PDF_STYLES.colors.textMuted,
                lineHeight: 1.5,
                margin: [50, 0, 50, 24]
            },
            {
                text: 'by National HealthClaim',
                fontSize: PDF_STYLES.fontSize.sectionHeader,
                font: 'Merriweather',
                alignment: 'center',
                color: fillColor,
                bold: true,
                margin: [0, 0, 0, 0]
            }
        ]
    };

    let indemnityAgreement = {
        stack: [
            {
                text: 'Administrative Services Indemnity Agreement',
                fontSize: PDF_STYLES.fontSize.subtitle,
                font: 'Merriweather',
                alignment: 'center',
                color: PDF_STYLES.colors.textDark,
                bold: true,
                margin: [0, 0, 0, 20]
            },
            {
                text: [
                    { text: 'Made as of the ' },
                    { text: `${day}`, bold: true },
                    { text: ' day of ' },
                    { text: `${month}`, bold: true },
                    { text: ', ' },
                    { text: `${year}`, bold: true }
                ],
                fontSize: PDF_STYLES.fontSize.body,
                alignment: 'center',
                lineHeight: 1.5,
                margin: [0, 0, 0, 20]
            },
            {
                columns: [
                    { width: '*', text: '' },
                    {
                        width: 'auto',
                        table: {
                            widths: [100, 180],
                            body: [
                                [
                                    { text: 'Between:', fontSize: PDF_STYLES.fontSize.body, color: PDF_STYLES.colors.textMuted, border: [false, false, false, false] },
                                    { text: 'National HealthClaim Corporation ("NHC")', fontSize: PDF_STYLES.fontSize.body, bold: true, color: fillColor, border: [false, false, false, false] }
                                ],
                                [
                                    { text: 'and the Subscriber:', fontSize: PDF_STYLES.fontSize.body, color: PDF_STYLES.colors.textMuted, border: [false, false, false, false] },
                                    { text: policyName, fontSize: PDF_STYLES.fontSize.body, bold: true, color: fillColor, border: [false, false, false, false] }
                                ]
                            ]
                        },
                        layout: 'noBorders'
                    },
                    { width: '*', text: '' }
                ],
                margin: [0, 0, 0, 30]
            }
        ],
        pageBreak: 'before'
    };

    let indemnityLegal = [
        {
            text: [{
                text: 'WHEREAS',
                bold: true
            }, ' NHC and the Subscriber wish to enter into an indemnity contract whereby NHC, for consideration, agrees to indemnify certain persons in respect of certain hospital expenses, medical expenses, medical plans or any combination of such expenses or plans;'],
            fontSize: 10,
            alignment: 'left',
            margin: [0, 0, 0, 10]
        }, {
            text: [{
                text: 'NOW THEREFORE THIS AGREEMENT WITNESSETH',
                bold: true
            }, ' that in consideration of the mutual premises and covenants herein contained, the receipt and sufficiency of which is hereby acknowledged, it is agreed that:'],
            fontSize: 10,
            alignment: 'left',
            margin: [0, 0, 0, 10]
        }, {
            text: 'Definitions',
            fontSize: 11,
            bold: true,
            margin: [0, 16, 0, 8],
            color: fillColor
        }, {
            text: '1. The following definitions shall be used throughout this agreement:',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            ul: [{
                text: ['a. "Administrative Fee" means the fee to be paid by the Subscriber to NHC concurrently with the Eligible Medical Expenditure, as outlined in Schedule "B", plus any applicable taxes payable thereon, including Goods and Services Tax and any applicable Provincial taxes;'],
                margin: [10, 0, 0, 10],
                listType: 'none'
            }, {
                text: ['b. "Claim Adjudication" means the process whereby NHC adjudicates a claim of a Qualified Person to determine, among other things, whether such claim is a type which is covered by the PHSP and whether such claim has been paid by the Qualified Person;'],
                margin: [10, 0, 0, 10],
                listType: 'none'
            }, {
                text: ['c. "Eligible Medical Services" means only those hospital expenses, medical expenses, medical plans or any combination of such expenses or plans which, pursuant to the interpretation by NHC of the Income Tax Act (Canada), including section 248(1) and section 118.2(2) thereof and regulations thereunder, may be included in the PHSP;'],
                margin: [10, 0, 0, 10],
                listType: 'none'
            }, {
                text: ['d. "Eligible Medical Expenditure" means expenditures in respect of Eligible Medical Services provided to a Qualified Person, up to maximum amounts as set out in Schedule "A" hereto;'],
                margin: [10, 0, 0, 10],
                listType: 'none'
            }, {
                text: ['e. "Eligible Lifestyle Expenditure" means expenditure in respect of Lifestyle Expenses provided to a Qualified Person, up to maximum amounts as set out in Schedule "A" hereto;'],
                margin: [10, 0, 0, 10],
                listType: 'none'
            }, {
                text: ['f. "Fee Guide" means the Schedule of Administrative Fees which is attached as Schedule "B" hereto, forming part of this Agreement;'],
                margin: [10, 0, 0, 10],
                listType: 'none'
            }, {
                text: ['g. "PHSP" means the private health services plan (known as the Health Spending Account)to be established and administered pursuant to this indemnity contract by NHC on behalf of the Subscriber wherein, upon a Qualified Person making an Eligible Medical Expenditure, the Subscriber shall remit the Premium to NHC, following which, upon a satisfactory Claim Adjudication, NHC shall indemnify the Qualified Person for the cost of the Eligible Medical Expenditure; '],
                margin: [10, 0, 0, 10],
                listType: 'none'
            }, {
                text: ['h. "Premium" means the sum of the Eligible Medical Expenditure, and/or Eligible Lifestyle Expenditure (depending on the type of plan chosen by the Subscriber), the Administrative Fee, and any applicable taxes payable thereon, including Goods and Services Tax and any applicable Provincial taxes;'],
                margin: [10, 0, 0, 10],
                listType: 'none'
            }, {
                text: ['i. "Qualified Person" means such employees of the Subscriber and other persons as are set out in Schedule "A" hereto who may, depending on the plan offered by the Subscriber, include the spouse or common-law partner of the employee (including same-sex common-law partners) or any member of that employee\'s household who is a dependent of that employee, as defined for purposes of the Canada Income Tax Act, so long as there is a contract of employment in good standing.'],
                margin: [10, 0, 0, 10],
                listType: 'none'
            },],
            margin: [20, 0, 0, 10], listType: 'none',
            fontSize: 10,
        }, {
            text: 'Covenants of the Subscriber',
            fontSize: 11,
            bold: true,
            margin: [0, 16, 0, 8],
            color: fillColor
        }, {
            text: '2. Upon receipt of a claim from a Qualified Person for reimbursement of a Medical Expenditure and/or Lifestyle Expenditure, the Subscriber shall pay the Premium and applicable taxes to NHC, for NHC to perform the Claim Review and Settlement process.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: '3. The Subscriber shall promptly provide funding to settle approved claims with payment options and processing policy outlined in Schedule "C" hereto.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: '4. The Subscriber shall keep NHC claim processing systems current as described in Schedule "A" hereto.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: 'Covenants of NHC',
            fontSize: 11,
            bold: true,
            margin: [0, 16, 0, 8],
            color: fillColor
        }, {
            text: '5. Will commence the Claim Adjudication process upon receipt of the claim details either through the mobile application (ClaimSnap), the online portal, or via post mail, subject to the funding policy in Schedule "C" hereto.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: '6. If the Subscriber pays through Pre-authorized Debit (PAD), direct deposit, online payment, or wire transfer, NHC shall follow the steps listed in paragraph 8.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: '7. In the event that, through the Claim Adjudication process, NHC determines that the claim is one which is covered by the Health Spending Account, it shall:',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: '8. Subject to paragraph 6 and 7 herein, upon receipt of the Premium NHC shall:',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            ul: [{
                text: ['a. maintain the Premium on behalf of the Subscriber until such time as NHC either:'],
                margin: [10, 0, 0, 10],
                listType: 'none'
            }, {
                text: ['i. pays the amount of the Eligible Medical Expenditure and/or Eligible Lifestyle Expenditure to the Qualified Person and subsequently transfers the Administrative Fee to itself, all in accordance with paragraph 6 and 7 herein; or'],
                margin: [20, 0, 0, 10],
                listType: 'none'
            }, {
                text: ['ii. returns the Premium to the Subscriber pursuant to paragraph 10 herein.'],
                margin: [20, 0, 0, 10],
                listType: 'none'
            },], margin: [20, 0, 0, 10], listType: 'none', fontSize: 10,
        }, {
            text: '9. In the event that, through the Claim Adjudication process, NHC determines that the claim is one which is covered by the Spending Account, it shall:',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            ul: [{
                text: ['a. indemnify that Qualified Person in respect of such claim by issuing a payment through direct deposit or cheque in the amount of such Eligible Medical Expenditure and/or Eligible Lifestyle Expenditure to such Qualified Person;'],
                margin: [10, 0, 0, 10],
                listType: 'none'
            }, {
                text: ['b. issue a Premium statement/invoice to the Subscriber; and\n'],
                margin: [10, 0, 0, 10],
                listType: 'none'
            }, {
                text: ['c. transfer the Administrative Fee to its own account'],
                margin: [10, 0, 0, 10],
                listType: 'none'
            },], margin: [20, 0, 0, 10], listType: 'none', fontSize: 10,
        }, {
            text: '10. In the event that, through the Claim Adjudication process, NHC determines for any reason that the claim is not one which is covered by the Spending Account, it shall either return the Premium to the Subscriber (if requested by the Subscriber), or hold the balance of Premium in the Subscriber\'s account for future use, if funds are already received. NHC will then inform the Qualified Person with an explanation of why the claim is not covered.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: '11. NHC shall use commercially reasonable efforts to ensure that the Claim Adjudication process and the indemnification of a Qualified Person (if required) are completed in a timely manner, subject to Schedule "C" hereto.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: '12. NHC shall not become entitled to the Administrative Fee unless and until the cheque or payment in the amount of the Eligible Medical Expenditure (if required) and/or Eligible Lifestyle Expenditure (if required) has been issued to the Qualified Person.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: '13. NHC reserves the right to change the Administrative Fee on 60 days\' written notice to the Subscriber.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: 'Conditions Precedent',
            fontSize: 11,
            bold: true,
            margin: [0, 16, 0, 8],
            color: fillColor
        }, {
            text: '14. Conditions precedent to the performance of this indemnity agreement are that:\n',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            ul: [{
                text: ['a. there shall be a contract of employment in good standing between the Subscriber and the Qualified Person or the person through whom such Qualified Person makes a claim pursuant to the Spending Account; and'],
                margin: [10, 0, 0, 10],
                listType: 'none',
                fontSize: 10,
            }, {
                text: ['b. the Subscriber shall have an undertaking or a contractual obligation to the Qualified Persons, and each of them, to indemnify such Qualified Persons in respect of Eligible Medical Expenses and/or Eligible Lifestyle Expenses; and that by submitting a Premium and documentation to NHC pursuant to paragraph 2 herein, the Subscriber represents to NHC that such conditions precedent exist.'],
                margin: [10, 0, 0, 10],
                listType: 'none'
            },], margin: [20, 0, 0, 10], listType: 'none', fontSize: 10,
        }, {
            text: 'Enforcement by Qualified Persons',
            fontSize: 11,
            bold: true,
            margin: [0, 16, 0, 8],
            color: fillColor
        }, {
            text: '15. NHC agrees that Qualified Persons are entitled to enforce NHC\'s obligations to indemnify them pursuant to this agreement notwithstanding that such Qualified Persons are not named, nor have they signed, as parties to this agreement and that in the event of such enforcement, NHC shall not raise the issue of privity of contract.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: 'Termination of Agreement',
            fontSize: 11,
            bold: true,
            margin: [0, 16, 0, 8],
            color: fillColor
        }, {
            text: '16. The contract may be terminated',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            ul: [{
                text: ['a. By NHC giving the Subscriber 15 days notice of termination by email.'],
                margin: [10, 0, 0, 10],
                listType: 'none'
            }, {
                text: ['b. By the Subscriber at any time on request, subject to no outstanding claim payment funding.'],
                margin: [10, 0, 0, 10],
                listType: 'none'
            },], margin: [20, 0, 0, 10], listType: 'none', fontSize: 10,
        }, {
            text: '17. If the contract is terminated by NHC',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            ul: [{
                text: ['a. NHC must refund the excess Premium in the Subscriber\'s account that was held for future claim reimbursement; and'],
                margin: [10, 0, 0, 10],
                listType: 'none'
            }, {
                text: ['b. The refund must accompany the notice unless the Premium is subject to adjustment or determination as to amount, in which case the refund must be made as soon as practicable.'],
                margin: [10, 0, 0, 10],
                listType: 'none'
            },], margin: [20, 0, 0, 10], listType: 'none', fontSize: 10,
        }, {
            text: '18. If the contract is terminated by the Subscriber (company/policy holder), NHC must refund as soon as practicable the excess Premium in the Subscriber\'s account that was held for future claim reimbursement.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: '19. The 15 day period referred to in subparagraph 16.a of this condition starts to run on the day the email is delivered to the Subscriber\'s address.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: 'General',
            fontSize: 11,
            bold: true,
            margin: [0, 16, 0, 8],
            color: fillColor
        }, {
            text: '20. This Agreement shall be governed by and construed in accordance with the laws of the Province of Alberta and the laws of Canada applicable therein.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: '21. The Parties agree that the within agreement is an indemnity contract in respect of hospital expenses, medical expenses, lifestyle expenses, or any combination of such expenses pursuant to the Canada Income Tax Act, including section 248(1) therein and that they will construe and interpret this agreement accordingly.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: '22. Headings used in this Agreement are used for convenience only and shall not form the basis for any interpretation of the terms of intent of this Agreement.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: '23. If one or more of the provisions of this agreement or any part of them is, or adjudged to be, invalid, illegal or unenforceable in any respect, the validity, legality and enforceability of the remaining provisions hereof shall not in any way be affected or impaired thereby, and such invalid, illegal or unenforceable provision or part shall be deemed to be severable.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: '24. The Subscriber acknowledges that it has had the opportunity to obtain its own legal and tax advice with respect to this agreement.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: '25. Schedules "A", "B", and "C" referred to herein and attached hereto are incorporated by reference to and form part of this agreement.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: '26. This agreement may be executed and delivered in separate counterparts and by electronic means, each of which when so executed and delivered shall constitute the one in the same instrument.',
            fontSize: 10,
            margin: [0, 0, 0, 10]
        }, {
            text: `Signed this ${day} day of ${month}, ${year}.`, fontSize: 10, bold: true, margin: [0, 20, 0, 30]
        }]

    let eligibleExpensesContent = [];
    if (selectedPlan !== "hsa") {
        eligibleExpensesContent.push({
            text: 'Eligible LSA Expenses',
            fontSize: PDF_STYLES.fontSize.sectionHeader,
            font: 'Merriweather',
            bold: true,
            margin: [0, 24, 0, 12],
            color: fillColor
        });

        const savedExpenses = Array.isArray(companyInfo.eligibleLifestyleExpenses) ? companyInfo.eligibleLifestyleExpenses : [];
        const isPayAsSubmitted = savedExpenses.includes(PAY_AS_SUBMITTED_SENTINEL);

        if (isPayAsSubmitted) {
            eligibleExpensesContent.push({
                text: "Pay As Submitted — any receipt submitted for any expense will be eligible for reimbursement; no category restrictions apply.",
                fontSize: PDF_STYLES.fontSize.body,
                color: PDF_STYLES.colors.textDark,
                margin: [0, 0, 0, 10]
            });
            const nonTaxable = savedExpenses.filter(expense =>
                expense === "PPE Footwear (Work Boots only) NON-TAXABLE" ||
                expense === "PPE Reflective Clothing & Equipment NON-TAXABLE" ||
                expense === "Hunting & Trapping - Equipment, gear, and apparel"
            );
            if (nonTaxable.length > 0) {
                eligibleExpensesContent.push({
                    ul: nonTaxable.map(expense => ({
                        text: expense,
                        fontSize: PDF_STYLES.fontSize.body,
                        color: PDF_STYLES.colors.textDark,
                        margin: [0, 0, 0, 4]
                    })),
                    margin: [16, 0, 0, 10]
                });
            }
            eligibleExpensesContent.push({
                text: "Note: If dependents are eligible in this policy, please be aware that including Pay-As-Submitted means that LSA dependent claims will be allowed at the category level.",
                bold: true,
                italics: true,
                fontSize: 10,
            });
        } else {
            eligibleExpensesContent.push({
                ul: savedExpenses.filter(expense => expense !== PAY_AS_SUBMITTED_SENTINEL).map(expense => ({
                    text: expense,
                    fontSize: PDF_STYLES.fontSize.body,
                    color: PDF_STYLES.colors.textDark,
                    margin: [0, 0, 0, 6]
                })),
                margin: [16, 0, 0, 10]
            });
        }
    }

    let getSignatureContent = (isDrawing, signature, imageURL, signatureImage) => {
        if (isDrawing) {
            return {
                image: imageURL, fit: [150, 100]
            };
        } else {
            return {
                text: signature, font: 'DarceyOliver', fontSize: 80,
                border: [false, false, false, false]
            };
        }
    }

    const generate = async () => {
        pdfMake.vfs = pdfMakeFonts.vfs;

        pdfMake.fonts = {
            DarceyOliver: {
                normal: 'DarceyOliver.otf',
                bold: 'DarceyOliver.otf',
                italics: 'DarceyOliver.otf',
                bolditalics: 'DarceyOliver.otf',
            },
            Merriweather: {
                normal: 'Merriweather-Regular.ttf',
                bold: 'Merriweather-Bold.ttf',
                italics: 'Merriweather-Italic.ttf',
                bolditalics: 'Merriweather-BoldItalic.ttf'
            },
            SourceSansPro: {
                normal: 'SourceSansPro-Regular.otf',
                bold: 'SourceSansPro-Bold.otf',
                italics: 'SourceSansPro-Italic.otf',
                bolditalics: 'SourceSansPro-BoldItalic.otf'
            },
        };

        const getImageBase64 = async (userLogo) => {
            const response = await fetch(userLogo);
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        };

        const logo = await getImageBase64(userLogo || '/nhclogo.png');

        // common table layout for clean tables
        const cleanTableLayout = {
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 6,
            paddingBottom: () => 6,
            hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length) ? 0.5 : 0.25,
            hLineColor: () => PDF_STYLES.colors.borderLight,
            vLineWidth: () => 0,
        };

        let docDefinition = {
            pageMargins: [40, 50, 40, 50],
            header: function (currentPage, pageCount) {
                return [{
                    canvas: [{
                        type: 'rect', x: 0, y: 0, w: 595.28, h: 6, color: fillColor
                    }]
                }];
            },
            footer: function (currentPage, pageCount) {
                return {
                    columns: [
                        { text: 'Enrollment Form', fontSize: 8, color: PDF_STYLES.colors.textLight, margin: [40, 0, 0, 0] },
                        { text: `Page ${currentPage} of ${pageCount}`, alignment: 'right', fontSize: 8, color: PDF_STYLES.colors.textLight, margin: [0, 0, 40, 0] }
                    ],
                    margin: [0, 15, 0, 0]
                };
            },
            content: [
                // logo and date row
                {
                    columns: [
                        { image: logo, width: 140 },
                        {
                            text: [
                                { text: 'Generated: ', fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted },
                                { text: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textDark }
                            ],
                            alignment: 'right',
                            margin: [0, 8, 0, 0]
                        }
                    ],
                    margin: [0, 0, 0, 16]
                },
                // title
                {
                    text: header,
                    font: 'Merriweather',
                    fontSize: PDF_STYLES.fontSize.title,
                    color: PDF_STYLES.colors.textDark,
                    margin: [0, 0, 0, 24]
                },
                // section A header
                {
                    text: 'Company Information',
                    font: 'Merriweather',
                    fontSize: PDF_STYLES.fontSize.sectionHeader,
                    bold: true,
                    color: fillColor,
                    margin: [0, 0, 0, 12]
                },
                // company info table
                {
                    margin: [0, 0, 0, 24],
                    table: {
                        widths: ['28%', '72%'],
                        body: [
                            [{ text: 'Company Name', bold: true, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted }, { text: policyName, fontSize: PDF_STYLES.fontSize.body }],
                            [{ text: 'Doing Business As', bold: true, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted }, { text: companyInfo.dba, fontSize: PDF_STYLES.fontSize.body }],
                            [{ text: 'Company Phone Number', bold: true, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted }, { text: formatPhoneNumber(companyInfo.companyPhone), fontSize: PDF_STYLES.fontSize.body }],
                            [{ text: 'Address 1', bold: true, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted }, { text: companyInfo.address1, fontSize: PDF_STYLES.fontSize.body }],
                            [{ text: 'Address 2', bold: true, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted }, { text: companyInfo.address2, fontSize: PDF_STYLES.fontSize.body }],
                            [{ text: 'City', bold: true, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted }, { text: companyInfo.city, fontSize: PDF_STYLES.fontSize.body }],
                            [{ text: 'Province', bold: true, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted }, { text: companyInfo.province, fontSize: PDF_STYLES.fontSize.body }],
                            [{ text: 'Postal Code', bold: true, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted }, { text: companyInfo.companyPostal, fontSize: PDF_STYLES.fontSize.body }],
                            [{ text: 'Advisor Name', bold: true, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted }, { text: companyInfo.brokerName, fontSize: PDF_STYLES.fontSize.body }],
                            [{ text: 'Advisor Email', bold: true, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted }, { text: companyInfo.brokerEmail, fontSize: PDF_STYLES.fontSize.body }],
                            [{ text: 'Policy Type', bold: true, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted }, { text: policyTypeLabel, fontSize: PDF_STYLES.fontSize.body }],
                            [{ text: 'Funding Option', bold: true, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted }, { text: selectedPADOption, fontSize: PDF_STYLES.fontSize.body }],
                            [{ text: 'Enrollment Notes', bold: true, fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted }, { text: companyInfo.companyNotes, fontSize: PDF_STYLES.fontSize.body }],
                        ]
                    },
                    layout: {
                        paddingLeft: () => 0,
                        paddingRight: () => 12,
                        paddingTop: () => 4,
                        paddingBottom: () => 4,
                        hLineWidth: (i, node) => (i === node.table.body.length) ? 0.5 : 0,
                        hLineColor: () => PDF_STYLES.colors.borderLight,
                        vLineWidth: () => 0,
                    }
                },
                // Plan Administrators section header
                {
                    text: 'Plan Administrators',
                    font: 'Merriweather',
                    fontSize: PDF_STYLES.fontSize.sectionHeader,
                    bold: true,
                    color: fillColor,
                    margin: [0, 0, 0, 8]
                },
                // Plan admins table (Role | Name | Email) — all admins in one table
                {
                    margin: [0, 0, 0, 24],
                    table: {
                        widths: ['28%', '32%', '40%'],
                        body: (() => {
                            const admins = (accountConfig.companyPlanAdmins || []);
                            const rows = admins.length > 0 ? admins : [{ firstName: '', lastName: '', email: '' }];
                            return [
                                [
                                    { text: 'Role', bold: true, fontSize: PDF_STYLES.fontSize.small, color: '#fff', fillColor: fillColor },
                                    { text: 'Name', bold: true, fontSize: PDF_STYLES.fontSize.small, color: '#fff', fillColor: fillColor },
                                    { text: 'Email', bold: true, fontSize: PDF_STYLES.fontSize.small, color: '#fff', fillColor: fillColor },
                                ],
                                ...rows.map((pa, i) => {
                                    const roleText = i === 0 ? 'Primary' : i === 1 ? 'Secondary' : `${i + 1}`;
                                    const fill = i % 2 === 0 ? PDF_STYLES.colors.backgroundAlt : 'white';
                                    return [
                                        { text: roleText, fontSize: PDF_STYLES.fontSize.small, fillColor: fill },
                                        { text: `${pa.firstName || ''} ${pa.lastName || ''}`.trim(), fontSize: PDF_STYLES.fontSize.small, fillColor: fill },
                                        { text: pa.email || '', fontSize: PDF_STYLES.fontSize.small, fillColor: fill },
                                    ];
                                }),
                            ];
                        })(),
                    },
                    layout: {
                        paddingLeft: () => 6,
                        paddingRight: () => 6,
                        paddingTop: () => 4,
                        paddingBottom: () => 4,
                        hLineWidth: () => 0.5,
                        hLineColor: () => PDF_STYLES.colors.borderLight,
                        vLineWidth: () => 0,
                    }
                },
                // section B header
                {
                    text: 'Employee Benefit Categories' + planLabel,
                    font: 'Merriweather',
                    fontSize: PDF_STYLES.fontSize.sectionHeader,
                    bold: true,
                    color: fillColor,
                    margin: [0, 0, 0, 8]
                },
                {
                    text: 'Employee Classes with annual spending limits. Each employee within a class receives the same limits.',
                    font: 'SourceSansPro',
                    fontSize: PDF_STYLES.fontSize.small,
                    color: PDF_STYLES.colors.textMuted,
                    lineHeight: 1.3,
                    margin: [0, 0, 0, 12],
                },
                // stacked per-category blocks (DEV-1583)
                ...categoryBlocks,
                // account configuration table
                {
                    table: {
                        widths: ['22%', '12%', '*'],
                        body: [
                            [
                                { text: 'Account Configuration', colSpan: 3, alignment: 'center', fillColor: fillColor, color: '#fff', bold: true, fontSize: PDF_STYLES.fontSize.body }, {}, {},
                            ],
                            ...configTableBody.map((row, rowIdx) => row.map((cell, cellIdx) => ({
                                text: typeof cell === 'object' ? cell.text : cell,
                                fillColor: rowIdx % 2 === 0 ? PDF_STYLES.colors.backgroundAlt : 'white',
                                fontSize: PDF_STYLES.fontSize.tableCell,
                                color: cellIdx === 2 ? PDF_STYLES.colors.textMuted : PDF_STYLES.colors.textDark,
                            })))
                        ]
                    },
                    layout: cleanTableLayout,
                },
                ...eligibleExpensesContent,
                // section C header
                {
                    text: 'Employee & Dependent Information',
                    font: 'Merriweather',
                    fontSize: PDF_STYLES.fontSize.sectionHeader,
                    bold: true,
                    color: fillColor,
                    margin: [0, 20, 0, 8]
                },
                ...employeeContent,
            // Indemnity Contract cover page
            {
                image: logo,
                width: 120,
                alignment: 'center',
                pageBreak: 'before',
                margin: [0, 30, 0, 0],
            },
            indemnityContract,
            indemnityAgreement,
            indemnityLegal,
            // Signature section - new page
            {
                text: 'Signatures',
                fontSize: PDF_STYLES.fontSize.sectionHeader,
                font: 'Merriweather',
                bold: true,
                color: fillColor,
                margin: [0, 0, 0, 16],
                pageBreak: 'before'
            },
            // Two-column signature layout
            {
                columns: [
                    // NHC side
                    {
                        width: '48%',
                        stack: [
                            { text: 'NATIONAL HEALTHCLAIM CORP.', fontSize: 10, bold: true, color: PDF_STYLES.colors.textDark, margin: [0, 0, 0, 4] },
                            { text: 'Shawna Zawada', fontSize: 10, color: PDF_STYLES.colors.textMuted, margin: [0, 0, 0, 12] },
                            { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 180, y2: 0, lineWidth: 0.5, lineColor: PDF_STYLES.colors.borderLight }], margin: [0, 0, 0, 4] },
                            { text: 'Print full name of Officer', fontSize: PDF_STYLES.fontSize.footnote, color: PDF_STYLES.colors.textLight, margin: [0, 0, 0, 16] },
                            { image: signatureImage, fit: [140, 60], margin: [0, 0, 0, 4] },
                            { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 180, y2: 0, lineWidth: 0.5, lineColor: PDF_STYLES.colors.borderLight }], margin: [0, 0, 0, 4] },
                            { text: 'Signature of Officer', fontSize: PDF_STYLES.fontSize.footnote, color: PDF_STYLES.colors.textLight, margin: [0, 0, 0, 16] },
                            { text: 'Director', fontSize: 10, color: PDF_STYLES.colors.textDark, margin: [0, 0, 0, 4] },
                            { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 180, y2: 0, lineWidth: 0.5, lineColor: PDF_STYLES.colors.borderLight }], margin: [0, 0, 0, 4] },
                            { text: 'Title of Officer', fontSize: PDF_STYLES.fontSize.footnote, color: PDF_STYLES.colors.textLight }
                        ]
                    },
                    // Spacer
                    { width: '4%', text: '' },
                    // Subscriber side
                    {
                        width: '48%',
                        stack: [
                            { text: 'THE SUBSCRIBER', fontSize: 10, bold: true, color: PDF_STYLES.colors.textDark, margin: [0, 0, 0, 4] },
                            { text: policyName, fontSize: 10, color: fillColor, margin: [0, 0, 0, 12] },
                            { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 180, y2: 0, lineWidth: 0.5, lineColor: PDF_STYLES.colors.borderLight }], margin: [0, 0, 0, 4] },
                            { text: 'Print full name of Subscriber', fontSize: PDF_STYLES.fontSize.footnote, color: PDF_STYLES.colors.textLight, margin: [0, 0, 0, 16] },
                            getSignatureContent(isDrawing, signature, imageURL),
                            { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 180, y2: 0, lineWidth: 0.5, lineColor: PDF_STYLES.colors.borderLight }], margin: [0, 4, 0, 4] },
                            { text: 'Signature of Subscriber', fontSize: PDF_STYLES.fontSize.footnote, color: PDF_STYLES.colors.textLight }
                        ]
                    }
                ],
                margin: [0, 0, 0, 30]
            },
            // Schedule A
            {
                text: '',
                pageBreak: 'before'
            },
            // Schedule A header
            {
                columns: [
                    { width: '*', text: '' },
                    {
                        width: 'auto',
                        stack: [
                            { text: 'Schedule "A"', fontSize: 14, font: 'Merriweather', bold: true, color: fillColor, alignment: 'center', margin: [0, 0, 0, 6] },
                            { text: 'to Administrative Service Indemnity Agreement', fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted, alignment: 'center', margin: [0, 0, 0, 4] },
                            { text: 'Subscriber Profile, Plan Design, and Employee Eligibility', fontSize: PDF_STYLES.fontSize.body, bold: true, color: PDF_STYLES.colors.textDark, alignment: 'center' }
                        ]
                    },
                    { width: '*', text: '' }
                ],
                margin: [0, 0, 0, 20]
            },
            {
                text: 'The complete details of the subscriber profile, Health Spending Account plan design, and the employee eligibility information is recorded in the NHC secure web site. It is the responsibility of the subscriber Plan Administrator to keep this information current.',
                alignment: 'center',
                margin: [30, 0, 30, 30],
                fontSize: PDF_STYLES.fontSize.body,
                color: PDF_STYLES.colors.textMuted,
                lineHeight: 1.4
            },
            // Schedule B header
            {
                columns: [
                    { width: '*', text: '' },
                    {
                        width: 'auto',
                        stack: [
                            { text: 'Schedule "B"', fontSize: 14, font: 'Merriweather', bold: true, color: fillColor, alignment: 'center', margin: [0, 0, 0, 6] },
                            { text: 'to Administrative Service Indemnity Agreement', fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted, alignment: 'center', margin: [0, 0, 0, 4] },
                            { text: 'Fees and Terms - Spending Account', fontSize: PDF_STYLES.fontSize.body, bold: true, color: PDF_STYLES.colors.textDark, alignment: 'center' }
                        ]
                    },
                    { width: '*', text: '' }
                ],
                margin: [0, 0, 0, 16]
            },
            // Fee details in a clean format
            {
                table: {
                    widths: ['auto', '*'],
                    body: [
                        [
                            { text: '1.', fontSize: PDF_STYLES.fontSize.body, bold: true, color: fillColor, border: [false, false, false, false] },
                            { text: `Enrollment Fee of $${enrollmentFee}.`, fontSize: PDF_STYLES.fontSize.body, border: [false, false, false, false] }
                        ],
                        [
                            { text: '2.', fontSize: PDF_STYLES.fontSize.body, bold: true, color: fillColor, border: [false, false, false, false] },
                            { text: `NHC and the Subscriber agree to an administration fee of ${adminFee}%.`, fontSize: PDF_STYLES.fontSize.body, border: [false, false, false, false] }
                        ],
                        [
                            { text: '3.', fontSize: PDF_STYLES.fontSize.body, bold: true, color: fillColor, border: [false, false, false, false] },
                            { text: 'Administration Fee - calculated as follows:', fontSize: PDF_STYLES.fontSize.body, border: [false, false, false, false] }
                        ]
                    ]
                },
                layout: 'noBorders',
                margin: [0, 0, 0, 8]
            },
            {
                ul: [
                    'Is a percentage of the employee benefits paid out under the Spending Account.',
                    'GST + applicable taxes are charged on the administration fee and/or the claim amount, dependent on the employee\'s province of residence.',
                    'Further details about the administrative fee can be found within the "Product" section of the Plan Administrator online portal.'
                ],
                margin: [30, 0, 0, 8],
                fontSize: PDF_STYLES.fontSize.body,
                color: PDF_STYLES.colors.textDark
            },
            {
                table: {
                    widths: ['auto', '*'],
                    body: [
                        [
                            { text: '4.', fontSize: PDF_STYLES.fontSize.body, bold: true, color: fillColor, border: [false, false, false, false] },
                            { text: 'Accounts must be funded within 120 days of claims being processed. Accounts not funded within 120 days will be placed on hold. Processing of new claims will automatically resume once funds have been received.', fontSize: PDF_STYLES.fontSize.body, border: [false, false, false, false] }
                        ]
                    ]
                },
                layout: 'noBorders',
                margin: [0, 0, 0, 24]
            },
            // Schedule C header
            {
                columns: [
                    { width: '*', text: '' },
                    {
                        width: 'auto',
                        stack: [
                            { text: 'Schedule "C"', fontSize: 14, font: 'Merriweather', bold: true, color: fillColor, alignment: 'center', margin: [0, 0, 0, 6] },
                            { text: 'to Administrative Service Indemnity Agreement', fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted, alignment: 'center', margin: [0, 0, 0, 4] },
                            { text: 'Account Funding Options and Procedures', fontSize: PDF_STYLES.fontSize.body, bold: true, color: PDF_STYLES.colors.textDark, alignment: 'center' }
                        ]
                    },
                    { width: '*', text: '' }
                ],
                margin: [0, 0, 0, 16]
            },
            {
                table: {
                    widths: ['auto', '*'],
                    body: [
                        [
                            { text: '1.', fontSize: PDF_STYLES.fontSize.body, bold: true, color: fillColor, border: [false, false, false, false] },
                            { text: 'The HealthCare Spending Account and/or Lifestyle Spending Account can be funded by the Subscriber in multiple ways with associated claim payment turnaround performance.', fontSize: PDF_STYLES.fontSize.body, border: [false, false, false, false] }
                        ]
                    ]
                },
                layout: 'noBorders',
                margin: [0, 0, 0, 8]
            },
            {
                ul: [
                    'The fastest claim payment turnaround time is achieved using Pre-Authorized Debit (PAD). A weekly PAD pull is performed by NHC to top up the funding balance to a predetermined level. During the time between PAD pulls, claims are paid using the current account funds. A separate PAD agreement is required to enable this service.',
                    'Online payment or Direct Deposit options allow the Subscriber to fund their account as desired through their banking services. Funding credit will be applied upon notification by NHC\'s bank and will be available for immediate use.',
                    'Funding with Wire Transfer is supported and will be available for immediate use.',
                    'Funding by cheque is supported, however, a 10-day holding period is required to ensure the funds have cleared.'
                ],
                margin: [30, 0, 0, 8],
                fontSize: PDF_STYLES.fontSize.body,
                color: PDF_STYLES.colors.textDark
            },
            {
                table: {
                    widths: ['auto', '*'],
                    body: [
                        [
                            { text: '2.', fontSize: PDF_STYLES.fontSize.body, bold: true, color: fillColor, border: [false, false, false, false] },
                            { text: 'Subscribers who haven\'t paid submitted claims for more than 120 days because of insufficient balance on hand will face a hold on processing any new claims. Processing of new and held claims will automatically resume once funds have been received.', fontSize: PDF_STYLES.fontSize.body, border: [false, false, false, false] }
                        ]
                    ]
                },
                layout: 'noBorders',
                margin: [0, 0, 0, 10]
            },
            ...(requiresBackdatingAck && backdatingAck ? [
                {
                    text: '',
                    pageBreak: 'before'
                },
                {
                    stack: [
                        ...(userLogo ? [{ image: userLogo, width: 80, alignment: 'center', margin: [0, 0, 0, 8] }] : []),
                        { text: 'National HealthClaim Corp.', fontSize: PDF_STYLES.fontSize.body, bold: true, color: PDF_STYLES.colors.textDark, alignment: 'center' },
                        { text: '5320 - 7005 Fairmount Dr. SE - Building 5000', fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted, alignment: 'center' },
                        { text: 'Calgary, AB T2H 0K4', fontSize: PDF_STYLES.fontSize.small, color: PDF_STYLES.colors.textMuted, alignment: 'center' }
                    ],
                    margin: [0, 0, 0, 20]
                },
                {
                    text: 'BACKDATING HEALTH SPENDING ACCOUNT ACKNOWLEDGMENT',
                    fontSize: PDF_STYLES.fontSize.subtitle,
                    font: 'Merriweather',
                    bold: true,
                    color: fillColor,
                    alignment: 'center',
                    margin: [0, 0, 0, 20]
                },
                {
                    text: 'This acknowledgment form is to be signed by a director of the company to authorize the backdating of the enrollment (up to 24 months) from the year end of the plan to process Health Spending Account claim receipt(s) for all eligible plan members.',
                    fontSize: PDF_STYLES.fontSize.body,
                    margin: [0, 0, 0, 10]
                },
                {
                    text: [
                        { text: 'By signing this acknowledgment, you are agreeing and confirming that the company was incorporated and active; and all employees that are receiving the backdate advantage have been actively employed on the Plan Effective Date.', bold: true },
                        { text: ' All claims that are being submitted have not been submitted with any other insurance plan or claimed as a medical expense tax credit on the employee\'s personal tax return.' }
                    ],
                    fontSize: PDF_STYLES.fontSize.body,
                    margin: [0, 0, 0, 10]
                },
                {
                    text: 'It is acknowledged that once the current (and backdated) benefit year has completed, the Health Spending Account will follow a conventional 12 month cycle for the upcoming terms.',
                    fontSize: PDF_STYLES.fontSize.body,
                    margin: [0, 0, 0, 14]
                },
                {
                    text: '** Important **',
                    fontSize: PDF_STYLES.fontSize.body,
                    bold: true,
                    alignment: 'center',
                    margin: [0, 0, 0, 10]
                },
                {
                    text: 'National HealthClaim strives to align its operational procedures and enforcement of claim receipt dates with the Canadian Revenue Agency (CRA) guidelines for Health Spending Accounts. Please be advised that in the unlikely event of an audit, backdated expenses may not be allowed if contested and is the responsibility of the company and its directors.',
                    fontSize: PDF_STYLES.fontSize.body,
                    margin: [0, 0, 0, 20]
                },
                {
                    stack: [
                        { text: 'Company Name', fontSize: PDF_STYLES.fontSize.footnote, color: PDF_STYLES.colors.textLight },
                        { text: backdatingAck.companyName || '', fontSize: PDF_STYLES.fontSize.body, color: PDF_STYLES.colors.textDark, margin: [0, 2, 0, 2] },
                        { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 400, y2: 0, lineWidth: 0.5, lineColor: PDF_STYLES.colors.borderLight }], margin: [0, 0, 0, 14] }
                    ]
                },
                {
                    stack: [
                        { text: 'Director Name', fontSize: PDF_STYLES.fontSize.footnote, color: PDF_STYLES.colors.textLight },
                        { text: backdatingAck.directorName || '', fontSize: PDF_STYLES.fontSize.body, color: PDF_STYLES.colors.textDark, margin: [0, 2, 0, 2] },
                        { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 400, y2: 0, lineWidth: 0.5, lineColor: PDF_STYLES.colors.borderLight }], margin: [0, 0, 0, 14] }
                    ]
                },
                {
                    stack: [
                        { text: 'Director Authorization', fontSize: PDF_STYLES.fontSize.footnote, color: PDF_STYLES.colors.textLight },
                        backdatingAck.signature && backdatingAck.signature.startsWith('data:image')
                            ? { image: backdatingAck.signature, fit: [180, 60], margin: [0, 2, 0, 2] }
                            : { text: backdatingAck.signature || '', font: 'DarceyOliver', fontSize: 40, color: PDF_STYLES.colors.textDark, margin: [0, 2, 0, 2] },
                        { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 400, y2: 0, lineWidth: 0.5, lineColor: PDF_STYLES.colors.borderLight }], margin: [0, 0, 0, 14] }
                    ]
                },
                {
                    stack: [
                        { text: 'Date', fontSize: PDF_STYLES.fontSize.footnote, color: PDF_STYLES.colors.textLight },
                        { text: backdatingAck.signedDate || '', fontSize: PDF_STYLES.fontSize.body, color: PDF_STYLES.colors.textDark, margin: [0, 2, 0, 2] },
                        { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 200, y2: 0, lineWidth: 0.5, lineColor: PDF_STYLES.colors.borderLight }], margin: [0, 0, 0, 24] }
                    ]
                },
                {
                    text: '***This document is used when backdating enrollment beyond the current benefit year***',
                    fontSize: PDF_STYLES.fontSize.small,
                    italics: true,
                    alignment: 'center',
                    color: PDF_STYLES.colors.textMuted,
                    margin: [0, 0, 0, 6]
                },
                {
                    text: 'Backdate Acknowledgment Fillable | Updated April 17, 2026 | External',
                    fontSize: PDF_STYLES.fontSize.footnote,
                    alignment: 'center',
                    color: PDF_STYLES.colors.textLight
                }
            ] : [])
            ],
            defaultStyle: {
                font: 'SourceSansPro',
                fontSize: PDF_STYLES.fontSize.body,
                color: PDF_STYLES.colors.textDark,
                lineHeight: 1.3
            },
            styles: {
                sectionHeader: {
                    font: 'Merriweather',
                    fontSize: PDF_STYLES.fontSize.sectionHeader,
                    bold: true
                },
                tableHeader: {
                    bold: true,
                    fontSize: PDF_STYLES.fontSize.tableCell
                }
            }
        };

        // pdfMake.createPdf(docDefinition).open();
        return docDefinition;
        // return new Promise((resolve, reject) => {
        //     pdfMake.createPdf(docDefinition).getBuffer(buffer => {
        //         const byteArray = new Uint8Array(buffer);
        //         const base64String = btoa(byteArray.reduce((data, byte) => data + String.fromCharCode(byte), ''));
        //         resolve(base64String);
        //     });
        // });
    }

    useImperativeHandle(ref, () => ({
        generate
    }));

    return null;
}));

export default PDFGenerator;